import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { TextField, Typography, IconButton, Autocomplete } from "@mui/material";
import { Formik, Form, Field } from 'formik';
import { Input } from 'reactstrap';
import { GetSchema, email_required } from '../../../js/Base/ValidationSchema';
import toastAlert from '../../../js/Base/ToastAlert';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from "@mui/icons-material/Add";
import { getProjectMembersByProjectId } from '../../../api/projectApi';
import { inviteCompanyMember } from '../../../api/companyApi';

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const CompanyMemberInviteModal = ({ isModalOpen, handleClose, submitEffect }) => {
  const [showAddArea, setShowAddArea] = useState(false);
  const [rows, setRows] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    getProjectMembers();
  }, []);
  
  const getProjectMembers = async () => {
    const projectUuid = localStorage.getItem("project_uuid");
    const userName = localStorage.getItem('user_email');
    if (!projectUuid) {
      toastAlert.autoClose(true).showError("查無資料，請聯絡系統管理員");
      return;
    }

    try {
      const res = await getProjectMembersByProjectId(userName, projectUuid);
      const data = res.data;

      if (data.success) {
        setRows(data.members.map(m => m.email));
      } else {
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
      }

    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };

  const modal_btn = {
    width: '94px',
    height: '36px',
    textAlign: 'center',
    fontSize: '14px'
  };

  const add_area ={
    width: '92%',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#e9e9f0',
    padding: '2% 3%',
    marginTop: '5px',
    marginBottom: '10px',
  };

  const add_member_btn = {
    width: 35,
    height: 40,
    borderRadius: 4,
    border: '1px solid #d9d9d9',
    background: '#fff',
    marginLeft: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    cursor: 'pointer'
  };

  const btn_add = {
    display: 'inline-block',
    width: '20%',
    height: '100%',
    border: '1px solid #bdbdbd',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    fontSize: '12px',
    opacity: '1',
    color: '#323232',
    margin: '0px',
  };

  const formSchema = GetSchema({
    email: email_required(),
  });

  const handleAddEmail = (values, setFieldValue) => {
    if (!newEmail) {
      setErrorText("請輸入 Email");
      return;
    }
  
    if (!validateEmail(newEmail)) {
      setErrorText("無效的 Email 格式");
      return;
    }
  
    // 檢查下拉清單
    const existsInRows = rows.some(
      (r) =>
        (typeof r === "string" ? r : r?.email ?? "")
          .toLowerCase() === newEmail.toLowerCase()
    );
  
    if (existsInRows) {
      setShowAddArea(false);
      setNewEmail("");
      setErrorText("");
      return;
    }
  
    // 去除重複機制
    const updatedEmails = [...(values.email || []), newEmail].filter(
      (v, i, arr) =>
        arr.findIndex(
          (x) =>
            (typeof x === "string" ? x : x?.email ?? "") ===
            (typeof v === "string" ? v : v?.email ?? "")
        ) === i
    );
  
    setFieldValue("email", updatedEmails);
    setShowAddArea(false);
    setNewEmail("");
    setErrorText("");
  };
  

  const handleSubmit = async (values, { resetForm, setErrors }) => {
    try {
      const companyUuid = localStorage.getItem("company_uuid");
      if (!companyUuid) {
        toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
        return;
      }
      const payload = {
        company_uuid: companyUuid,
        members: values.email, // 如果是單個 email，保持原本格式
      };

      const response = await inviteCompanyMember(payload);
      const data = response.data
      if (data.success && data.success_list?.length > 0) {
        const emailList = data.success_list.join('\n');
        toastAlert.autoClose(true).showSuccess(`成員新增成功:\n${emailList}`);
      }
      if (data.fail_list?.length > 0) {
        const showFail = () => {
          const failMsg = data.fail_list
            .map((f) => {
              if (f.error_code === 1) {
                return `${f.email} (此信箱使用者已為公司成員)`;
              }
              return `${f.email} (發生錯誤)`;
            })
            .join("\n");
      
          toastAlert.autoClose(true).showError(
            `成員新增失敗:\n${failMsg}`
          );
        };
      
        if (data.success_list?.length > 0) {
          setTimeout(showFail, 2000);
        } else {
          showFail();
        }
      }
  
      if (data.success_list?.length > 0) {
        resetForm();
        if (submitEffect) submitEffect();
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    } finally {
      handleClose();
    }
  };

  return (
    <>
      <Modal
        animation="true"
        style={{ zIndex: '1299' }}
        show={isModalOpen}
        onHide={handleClose}
        className="CompanyManagement"
      >
        <Modal.Header>
          <Modal.Title className="modal-title">人員邀請</Modal.Title>
        
          {/* 解決籃框問題，自訂MUI按鈕 */}
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto'
            }}
          >
            <CloseIcon fontSize="medium" />
          </button>
        </Modal.Header>
        <Formik
          initialValues={{ email: [] }}
          onSubmit={handleSubmit}
          // validationSchema={formSchema()}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <Form className="form" style={{ padding:'0px' }}>
              <Modal.Body className="modal-body" style={{ paddingTop:'0px' }}>
                <Row>
                  <p
                    style={{
                      fontSize: '12px',
                      color: '#323232',
                      fontWeight: 'bold',
                      marginTop: '20px',
                      marginBottom: '6px',
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                    className="form-title mb-0"
                  >
                    電子信箱
                  </p>
                    <Col md={12} style={{ whiteSpace: 'nowrap',display:'flex', alignItems: 'start', justifyContent:'start', marginTop:'6px' }}>
                      <Field name="email">
                        {({ field }) => (
                          <Autocomplete
                            // forcePopupIcon={false}     // 隱藏右邊下拉箭頭
                            // open={false}               // 強制關閉下拉
                            multiple
                            size="small"
                            limitTags={1}
                            // options={[]}
                            options={rows}
                            filterOptions={(x) => x}  // 避免顯示 "No options"
                            getOptionLabel={(option) => option}
                            // getOptionLabel={(option) => option.email || ''}

                            value={values.email}
                            onChange={(e, newValue) =>
                              setFieldValue("email", newValue)
                            }
                            sx={{
                              width: '95%',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#FFFFFF',
                                '& fieldset': {
                                  borderColor: '#E2E2E2',
                                },
                                '&:hover fieldset': {
                                  borderWidth: '1px',
                                  borderColor: '#7fbe25',
                                },
                                '&.Mui-focused fieldset': {
                                  borderWidth: '1px',
                                  borderColor: '#6AA318',
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                margin="none"
                                variant="outlined"
                                // onKeyDown={(e) => e.preventDefault()}
                                sx={{
                                  '& .MuiAutocomplete-endAdornment': {
                                    paddingTop: '0px',
                                    paddingBottom: '0px',
                                    marginBottom: '1px',
                                  },
                                  '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
                                    color: 'black',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    paddingTop: '0px',
                                    paddingBottom: '0px',
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </Field>
                      <IconButton
                        onClick={() => {
                          setNewEmail("");
                          setErrorText("");
                          setShowAddArea(true);
                        }}
                        sx={{
                          color: "black",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                          <AddIcon fontSize="medium" />
                        </Typography>
                    </IconButton>

                      {/* <button
                        type="button"
                        onClick={() => {
                          setNewEmail("");
                          setErrorText("");
                          setShowAddArea(true);
                        }}
                        style={add_member_btn}
                        aria-label="add"
                        title="add"
                      >
                        <AddIcon fontSize="medium" />
                      </button> */}
                    </Col>
                </Row>
                {showAddArea && (
                  <>
                    <Col md={12} className="d-flex align-items-center">
                      <div className="add-area" style={add_area}>
                        <Input
                          id="input-email"
                          type="text"
                          style={{ fontSize: '14px', width: '70%', marginRight: '15px' }}
                          onChange={(e) => setNewEmail(e.target.value)}
                        />
                        <Button
                          className="btn-add me-2"
                          style={btn_add}
                          onClick={() => {
                            setShowAddArea(false);
                            setNewEmail("");
                            setErrorText("");
                          }}
                        >
                          取消
                        </Button>
                        <Button
                          className="btn-add"
                          style={btn_add}
                          onClick={() => handleAddEmail(values, setFieldValue)}
                        >
                          確認
                        </Button>
                      </div>
                    </Col>
                  </>
                )}
                {errorText && (
                  <Col md={12}>
                    <p style={{ color: 'red', fontSize: '12px' }}>{errorText}</p>
                  </Col>
                )}
              </Modal.Body>
              <Modal.Footer className="modal-footer">
                <Button className="btn btn-custom" type="submit" disabled={isSubmitting}>
                  送出
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default CompanyMemberInviteModal;
